import axios from 'axios';
import { getSessionItem } from '../utils/safeStorage';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
const ADMIN_TOKEN_KEY = 'adminAccessToken';

const getAuthToken = (token) => token || getSessionItem(ADMIN_TOKEN_KEY) || null;

const authHeaders = (token) => ({
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
});

const handleError = (err) => {
  const message =
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    'Request failed';
  const error = new Error(message);
  error.status = err?.response?.status;
  throw error;
};

export const uploadPropertyImage = async (propertyId, file, token, options = {}) => {
  const authToken = getAuthToken(token);
  const formData = new FormData();
  formData.append('image', file);
  if (options.isCover) formData.append('is_cover', 'true');
  if (options.sortOrder) formData.append('sort_order', options.sortOrder);
  if (options.altText) formData.append('alt_text', options.altText);
  if (options.width) formData.append('width', options.width);
  if (options.height) formData.append('height', options.height);
  if (options.fileHash) formData.append('file_hash', options.fileHash);

  try {
    const res = await axios.post(`${API_BASE}/api/properties/${propertyId}/images`, formData, {
      headers: {
        ...authHeaders(authToken),
        'Content-Type': 'multipart/form-data',
      },
      withCredentials: true,
      onUploadProgress: (evt) => {
        if (!options.onProgress || !evt.total) return;
        const pct = Math.round((evt.loaded / evt.total) * 100);
        options.onProgress(pct);
      },
    });
    return res.data;
  } catch (err) {
    handleError(err);
  }
};

export const uploadDraftPropertyImage = async (file, token, options = {}) => {
  const authToken = getAuthToken(token);
  const formData = new FormData();
  formData.append('image', file);

  try {
    const res = await axios.post(`${API_BASE}/api/uploads`, formData, {
      headers: {
        ...authHeaders(authToken),
        'Content-Type': 'multipart/form-data',
      },
      withCredentials: true,
      onUploadProgress: (evt) => {
        if (!options.onProgress || !evt.total) return;
        const pct = Math.round((evt.loaded / evt.total) * 100);
        options.onProgress(pct);
      },
    });
    return res.data;
  } catch (err) {
    handleError(err);
  }
};

export const uploadHostDraftImage = async (file, token, options = {}) => {
  const authToken = getAuthToken(token);
  const formData = new FormData();
  formData.append('image', file);

  try {
    const res = await axios.post(`${API_BASE}/api/host/uploads`, formData, {
      headers: {
        ...authHeaders(authToken),
        'Content-Type': 'multipart/form-data',
      },
      withCredentials: true,
      onUploadProgress: (evt) => {
        if (!options.onProgress || !evt.total) return;
        const pct = Math.round((evt.loaded / evt.total) * 100);
        options.onProgress(pct);
      },
    });
    return res.data;
  } catch (err) {
    handleError(err);
  }
};

export const uploadHostPropertyImage = async (propertyId, file, token, options = {}) => {
  const authToken = getAuthToken(token);
  const formData = new FormData();
  formData.append('image', file);
  if (options.isCover) formData.append('is_cover', 'true');
  if (options.sortOrder) formData.append('sort_order', options.sortOrder);
  if (options.altText) formData.append('alt_text', options.altText);
  if (options.width) formData.append('width', options.width);
  if (options.height) formData.append('height', options.height);
  if (options.fileHash) formData.append('file_hash', options.fileHash);

  try {
    const res = await axios.post(`${API_BASE}/api/host/properties/${propertyId}/images/upload`, formData, {
      headers: {
        ...authHeaders(authToken),
        'Content-Type': 'multipart/form-data',
      },
      withCredentials: true,
      onUploadProgress: (evt) => {
        if (!options.onProgress || !evt.total) return;
        const pct = Math.round((evt.loaded / evt.total) * 100);
        options.onProgress(pct);
      },
    });
    return res.data;
  } catch (err) {
    handleError(err);
  }
};

export const reorderPropertyImages = async (propertyId, orderedImageIds, token) => {
  const authToken = getAuthToken(token);
  try {
    const res = await axios.put(
      `${API_BASE}/api/properties/${propertyId}/images/order`,
      { orderedImageIds },
      { headers: { ...authHeaders(authToken) }, withCredentials: true }
    );
    return res.data?.images || res.data;
  } catch (err) {
    handleError(err);
  }
};

export const setCoverImage = async (propertyId, coverImageId, token) => {
  const authToken = getAuthToken(token);
  try {
    const res = await axios.patch(
      `${API_BASE}/api/properties/${propertyId}/images/cover`,
      { coverImageId },
      { headers: { ...authHeaders(authToken) }, withCredentials: true }
    );
    return res.data;
  } catch (err) {
    handleError(err);
  }
};

export const deletePropertyImage = async (propertyId, imageId, token) => {
  const authToken = getAuthToken(token);
  try {
    const res = await axios.delete(`${API_BASE}/api/properties/${propertyId}/images/${imageId}`, {
      headers: { ...authHeaders(authToken) },
      withCredentials: true,
    });
    return res.data;
  } catch (err) {
    handleError(err);
  }
};

export const updateImageMeta = async (imageId, payload, token) => {
  const authToken = getAuthToken(token);
  try {
    const res = await axios.patch(`${API_BASE}/api/properties/images/${imageId}`, payload, {
      headers: { ...authHeaders(authToken) },
      withCredentials: true,
    });
    return res.data;
  } catch (err) {
    handleError(err);
  }
};

export const fetchPropertyImages = async (propertyId, token) => {
  const authToken = getAuthToken(token);
  try {
    const res = await axios.get(`${API_BASE}/api/properties/${propertyId}`, {
      headers: { ...authHeaders(authToken) },
      withCredentials: true,
    });
    return res.data?.images || [];
  } catch (err) {
    handleError(err);
  }
};


